/**
 * Campaign Frame Configuration
 *
 * This file defines the behavior and UI patterns for campaign frame mechanics.
 * Frames not in this config use default behavior.
 *
 * Pattern Types:
 * - ResourceHarvest: Harvest ingredients/resources from defeated creatures or environment
 * - FlavorCooking: Beast Feast dice matching minigame with meal rating
 * - NpcService: NPC-provided services (Berry's Restaurant)
 * - TokenPool: Corruption/transformation tracking (Wither tokens)
 * - DiceCheck: Standalone dice roll against table or threshold
 * - DayNightCycle: Environmental phase tracking (week-long cycles)
 * - DiseaseTracker: Progressive disease stages (Serpent's Sickness)
 * - PassiveInfo: Display-only information (lore, divinities)
 *
 * ============================================================================
 * CAMPAIGN FRAME SCHEMA VOCABULARY (LLM Output Contract)
 * ============================================================================
 *
 * Any frame config in FRAME_MECHANIC_CONFIG can include these fields.
 * Fields not present are treated as absent — no nulls needed.
 * Code must use optional chaining (e.g., frame?.contentOverrides?.equipment || []).
 *
 * REQUIRED:
 * @property {string} id - Unique frame identifier (kebab-case)
 * @property {string} name - Display name
 * @property {string} source - 'srd' | 'homebrew' | 'third-party'
 *
 * MECHANICS:
 * @property {Object<string, MechanicConfig>} mechanics - Pattern configs keyed by mechanicId
 * @property {string} downtimeOverride - Custom downtime move replacing default rest
 *
 * CONTENT OVERRIDES (homebrew can inject custom game content):
 * @property {Object} contentOverrides
 * @property {Array} contentOverrides.equipment - Custom equipment definitions
 * @property {Array} contentOverrides.ancestries - Custom ancestry definitions
 * @property {Array} contentOverrides.communities - Custom community definitions
 * @property {Array} contentOverrides.classes - Custom class definitions
 * @property {Array} contentOverrides.subclasses - Custom subclass definitions
 * @property {Array} contentOverrides.domainCards - Custom domain card definitions
 *
 * CHARACTER FLOW MODIFICATIONS:
 * @property {Array} characterCreationSteps - Extra steps in character builder
 * @property {Array} levelUpModifications - Changes to level-up flow
 * @property {Object} restOverrides - Custom rest mechanics
 *
 * CUSTOM CONTENT:
 * @property {Object<string, RollTable>} customRollTables - Reusable roll tables (d4-d20)
 * @property {Array} customTokenTypes - Custom token type definitions
 * @property {Array} customDowntimeMoves - Frame-specific downtime activities
 * @property {Array} customAdversaries - Frame-specific adversaries/NPCs
 * @property {Array} loreEntries - Worldbuilding text blocks
 * @property {Object} settingRules - Rule modifications/overrides
 *
 * METADATA:
 * @property {string} version - Semver string for homebrew versioning
 * @property {string[]} tags - Searchable tags for homebrew discovery
 */

// ============================================================================
// PATTERN DEFINITIONS
// ============================================================================

export const FRAME_MECHANIC_TYPES = {
  // Resource Mechanics
  RESOURCE_HARVEST: 'ResourceHarvest',     // Beast parts, blooms, environmental
  FLAVOR_COOKING: 'FlavorCooking',         // Dice matching minigame
  NPC_SERVICE: 'NpcService',               // Berry's Restaurant, vendors

  // Corruption/Transformation Mechanics (Witherwild)
  TOKEN_POOL: 'TokenPool',                 // Wither tokens, corruption tracking
  DICE_CHECK: 'DiceCheck',                  // Standalone dice roll against table/threshold

  // Environmental Mechanics
  DAY_NIGHT_CYCLE: 'DayNightCycle',        // Week-long phase cycling

  // Disease/Condition Mechanics
  DISEASE_TRACKER: 'DiseaseTracker',       // Progressive stage tracking

  // Downtime Mechanics
  DOWNTIME_MOVE: 'DowntimeMove',           // Player roll to replace GM roll (Keep Watch)

  // Display-Only
  PASSIVE_INFO: 'PassiveInfo',             // Lore, setting info, divinities

  // Political/War Mechanics (Five Banners Burning)
  FACTION_RELATIONS: 'FactionRelations',   // Nation relation grid tracking
  OBJECTIVE_COUNTDOWN: 'ObjectiveCountdown', // Time-limited countdown bars

  // Equipment/Currency Overrides (Motherboard)
  CURRENCY_OVERRIDE: 'CurrencyOverride',   // Replace gold with campaign currency
  EQUIPMENT_OVERRIDE: 'EquipmentOverride', // Replace standard weapons with campaign equipment

  // Colossal Adversary (Colossus of the Drylands)
  COLOSSAL_ADVERSARY: 'ColossalAdversary', // Multi-segment boss fight tracker
};

// ============================================================================
// COOKING CONFIGURATION
// ============================================================================

/**
 * Flavor profile definitions for cooking mechanics.
 * Each flavor maps to a die size and UI styling.
 */
export const FLAVOR_CONFIG = {
  Sweet: {
    die: 'd4',
    sides: 4,
    colorClass: 'bg-pink-100 text-pink-800',
    borderClass: 'border-pink-400',
    bgClass: 'bg-pink-500',
    icon: '🍬',
  },
  Salty: {
    die: 'd6',
    sides: 6,
    colorClass: 'bg-blue-100 text-blue-800',
    borderClass: 'border-blue-400',
    bgClass: 'bg-blue-500',
    icon: '🧂',
  },
  Bitter: {
    die: 'd8',
    sides: 8,
    colorClass: 'bg-green-100 text-green-800',
    borderClass: 'border-green-400',
    bgClass: 'bg-green-500',
    icon: '🥬',
  },
  Sour: {
    die: 'd10',
    sides: 10,
    colorClass: 'bg-yellow-100 text-yellow-800',
    borderClass: 'border-yellow-400',
    bgClass: 'bg-yellow-500',
    icon: '🍋',
  },
  Savory: {
    die: 'd12',
    sides: 12,
    colorClass: 'bg-red-100 text-red-800',
    borderClass: 'border-red-400',
    bgClass: 'bg-red-500',
    icon: '🍖',
  },
  Weird: {
    die: 'd20',
    sides: 20,
    colorClass: 'bg-purple-100 text-purple-800',
    borderClass: 'border-purple-400',
    bgClass: 'bg-purple-500',
    icon: '👁️',
  },
};

/**
 * Get flavor color class for UI styling
 * @param {string} flavor - Flavor profile name
 * @returns {string} - Tailwind color classes
 */
export const getFlavorColorClass = (flavor) => {
  return FLAVOR_CONFIG[flavor]?.colorClass || 'bg-gray-100 text-gray-800';
};

/**
 * Get flavor die size
 * @param {string} flavor - Flavor profile name
 * @returns {number} - Number of sides on the die
 */
export const getFlavorDieSides = (flavor) => {
  return FLAVOR_CONFIG[flavor]?.sides || 6;
};

// ============================================================================
// DICE MATCHING ALGORITHMS
// ============================================================================

/**
 * Algorithm definitions for dice matching mechanics.
 * These can be referenced by ID in mechanic configs.
 */
export const MATCHING_ALGORITHMS = {
  /**
   * Smart removal algorithm for Beast Feast cooking.
   * Optimizes matching chances by removing dice with lowest match probability.
   */
  'smart-removal': {
    id: 'smart-removal',
    name: 'Smart Removal',
    description: 'Automatically removes dice with lowest matching probability',
    selectWorstDie: (rolledDice) => {
      // Group dice by their sides (die type)
      const diceGroups = rolledDice.reduce((groups, die) => {
        if (!groups[die.sides]) groups[die.sides] = [];
        groups[die.sides].push(die);
        return groups;
      }, {});

      // Priority 1: Remove dice from groups with only one die (can't possibly match)
      const singletonGroups = Object.values(diceGroups).filter(group => group.length === 1);
      if (singletonGroups.length > 0) {
        // Among singletons, prefer removing larger dice (lower match probability)
        const largestSingleton = singletonGroups.reduce((worst, group) =>
          group[0].sides > worst[0].sides ? group : worst
        );
        return largestSingleton[0];
      }

      // Priority 2: From groups with multiple dice, remove the one with worst matching odds
      let worstDie = null;
      let worstMatchProbability = 1;

      for (const [sides, diceInGroup] of Object.entries(diceGroups)) {
        for (const die of diceInGroup) {
          const remainingOfSameType = diceInGroup.length - 1;
          const matchProbability = remainingOfSameType / parseInt(sides);

          if (matchProbability < worstMatchProbability) {
            worstMatchProbability = matchProbability;
            worstDie = die;
          }
        }
      }

      return worstDie || rolledDice[0];
    },
  },

  /**
   * Simple removal - just remove the first unmatched die.
   * Less optimal but simpler to understand.
   */
  'simple-removal': {
    id: 'simple-removal',
    name: 'Simple Removal',
    description: 'Removes the first unmatched die (no optimization)',
    selectWorstDie: (rolledDice) => rolledDice[0],
  },
};

// ============================================================================
// BENEFIT DISTRIBUTION CONFIGURATION
// ============================================================================

/**
 * Benefit types that can be distributed from cooking results.
 */
export const BENEFIT_TYPES = {
  hp: {
    id: 'hp',
    label: 'HP',
    icon: '❤️',
    description: 'Clear Hit Points',
    characterKey: 'currentHP',
    direction: 'decrease', // Lower is better for HP
    min: 0,
  },
  stress: {
    id: 'stress',
    label: 'Stress',
    icon: '💙',
    description: 'Clear Stress',
    characterKey: 'stress',
    direction: 'decrease',
    min: 0,
  },
  hope: {
    id: 'hope',
    label: 'Hope',
    icon: '✨',
    description: 'Gain Hope',
    characterKey: 'hope',
    direction: 'increase',
    max: (character) => character?.maxHope || 6,
  },
  scars: {
    id: 'scars',
    label: 'Scars',
    icon: '🩹',
    description: 'Clear Scars',
    characterKey: 'currentScars',
    direction: 'decrease',
    min: 0,
  },
};

// ============================================================================
// FRAME MECHANIC CONFIGURATIONS
// ============================================================================

export const FRAME_MECHANIC_CONFIG = {
  // ---------------------------------------------------------------------------
  // Beast Feast Campaign Frame
  // ---------------------------------------------------------------------------
  'beast-feast': {
    id: 'beast-feast',
    name: 'Beast Feast',
    source: 'srd',
    downtimeOverride: 'make-a-feast',

    mechanics: {
      /**
       * Harvest mechanic - collect ingredients from defeated creatures
       */
      harvest: {
        pattern: FRAME_MECHANIC_TYPES.RESOURCE_HARVEST,
        triggerLocation: 'combat-end',
        resourceKey: 'ingredients',
        // Yield table is in campaign_frames.json - this is just the pattern config
        yieldSource: 'customMechanics.harvesting.yields',
        flavorConfig: FLAVOR_CONFIG,
        ingredientTablesSource: 'customMechanics.ingredientTables',
      },

      /**
       * Bloom harvest - Hope-cost environmental gathering
       */
      blooms: {
        pattern: FRAME_MECHANIC_TYPES.RESOURCE_HARVEST,
        triggerLocation: 'exploration',
        resourceKey: 'ingredients',
        cost: { type: 'hope', amount: 1 },
        limitPerRest: 1,
        rollTableSource: 'customMechanics.blooms.rollTable',
        flavorConfig: FLAVOR_CONFIG,
        ingredientTablesSource: 'customMechanics.ingredientTables',
      },

      /**
       * Cooking mechanic - the main Beast Feast minigame
       */
      cooking: {
        pattern: FRAME_MECHANIC_TYPES.FLAVOR_COOKING,
        triggerLocation: 'downtime',
        inputResource: 'ingredients',
        flavorConfig: FLAVOR_CONFIG,
        matchingAlgorithm: 'smart-removal',
        benefitDistribution: ['hp', 'stress', 'hope', 'scars'],
        recipeBonus: {
          type: 'tierTokens',
          getAmount: (character) => character?.tier || 1,
        },
        cookbookTracking: true,
        aiGeneration: true,
        // Counts as rest for token refresh
        triggersRest: true,
      },

      /**
       * Berry's Restaurant - NPC food service
       */
      'berrys-restaurant': {
        pattern: FRAME_MECHANIC_TYPES.NPC_SERVICE,
        triggerLocation: 'downtime',
        cost: { type: 'gold', amount: 1 },
        maxUsesPerPC: 2,
        effects: [
          { id: 'clearStress', label: 'Clear Stress', icon: '💙' },
          { id: 'clearHP', label: 'Clear HP', icon: '❤️' },
          { id: 'gainHope', label: 'Gain Hope', icon: '✨' },
        ],
        descriptionSource: 'customMechanics.berrysRestaurant.description',
      },
    },
  },

  // ---------------------------------------------------------------------------
  // Witherwild Campaign Frame (from SRD)
  // ---------------------------------------------------------------------------
  'witherwild': {
    id: 'witherwild',
    name: 'The Witherwild',
    source: 'srd',

    mechanics: {
      /**
       * Wither Tokens - corruption tracking system
       * Gained when taking Severe damage from Withered adversaries
       */
      'wither-corruption': {
        pattern: FRAME_MECHANIC_TYPES.TOKEN_POOL,
        tokenKey: 'witherTokens',
        displayName: 'Wither Tokens',
        maxTokens: 20, // GM pool total
        triggerEvent: 'severe-damage-withered',
        triggerDescription: 'Mark a Wither token when you take Severe damage from a Withered creature',
        icon: '🌿',
        colorClass: 'bg-green-800 text-green-100',

        // What happens when tokens accumulate
        checkConfig: {
          checkDie: 'fear',
          threshold: 'lessThanOrEqual', // Roll <= tokens = fail
          onFail: {
            effect: 'gainScar',
            clearTokens: true,
            description: 'The Witherwild changes you permanently. Gain a scar describing your transformation.',
          },
        },

        // Session end behavior
        sessionEnd: {
          effect: 'convertToFear',
          clearTokens: true,
          description: 'At session end, any Wither tokens convert to Fear for the GM.',
        },

        // Death behavior
        onDeath: {
          effect: 'permanentCorruption',
          description: 'If you die with Wither tokens, you become Withered in death - your body transforms into corrupted flora.',
        },
      },

      /**
       * Day/Night Cycle - week-long environmental phases
       */
      'day-night-cycle': {
        pattern: FRAME_MECHANIC_TYPES.DAY_NIGHT_CYCLE,
        cycleDuration: 'week',
        phases: [
          {
            id: 'day',
            name: 'Day Week',
            icon: '☀️',
            description: 'The sun rises for a full week. Diurnal creatures are active, sun rose vines bloom.',
            environmentModifiers: {
              flora: ['sun-rose-vines', 'day-blooming-flowers'],
              fauna: 'diurnal',
              visibility: 'full',
            },
          },
          {
            id: 'night',
            name: 'Night Week',
            icon: '🌙',
            description: 'The sun sets for a full week. Nocturnal creatures emerge, night bloom flowers open.',
            environmentModifiers: {
              flora: ['night-bloom-flowers', 'luminescent-fungi'],
              fauna: 'nocturnal',
              visibility: 'reduced',
            },
          },
        ],
        gmControlled: true,
      },

      /**
       * Serpent's Sickness - progressive disease tracking
       */
      'serpents-sickness': {
        pattern: FRAME_MECHANIC_TYPES.DISEASE_TRACKER,
        diseaseName: "Serpent's Sickness",
        description: 'A plague spreading among Haven refugees, causing gradual petrification.',
        stages: [
          {
            id: 'healthy',
            name: 'Healthy',
            icon: '💚',
            description: 'No symptoms.',
            mechanicalEffects: null,
          },
          {
            id: 'cough',
            name: 'Dusty Cough',
            icon: '🤧',
            description: 'Coughing up fine bone-like dust.',
            mechanicalEffects: null,
          },
          {
            id: 'rash',
            name: 'Scaled Rash',
            icon: '🐍',
            description: 'Snake-like scales forming on skin.',
            mechanicalEffects: { presence: -1 },
          },
          {
            id: 'hardening',
            name: 'Hardening',
            icon: '🪨',
            description: 'Body beginning to petrify. Movement becomes difficult.',
            mechanicalEffects: { agility: -1, evasion: -1 },
          },
          {
            id: 'statue',
            name: 'Petrified',
            icon: '🗿',
            description: 'Turned completely to stone. Character is incapacitated.',
            mechanicalEffects: { incapacitated: true },
          },
        ],
        cure: {
          id: 'crimson-ladys-veil',
          name: "Crimson Lady's Veil",
          description: 'A rare flower that can cure any stage of the sickness.',
        },
        progressTrigger: 'gm-discretion',
      },

      /**
       * Faint Divinity - local gods with tribute mechanics (display only)
       */
      'faint-divinity': {
        pattern: FRAME_MECHANIC_TYPES.PASSIVE_INFO,
        displayName: 'Faint Divinities',
        description: 'Local gods of the Witherwild. Pay tribute for blessings, or risk curses.',
        divinities: [
          { id: 'fulgr', name: 'Fulgr, Stacker of Stones', tribute: 'smooth river rocks', domain: 'construction' },
          { id: 'hyacyins', name: 'Hyacyins, They Who Spread Small Blossoms', tribute: 'flowers', domain: 'growth' },
          { id: 'ikla', name: 'Ikla, Keeper of Forgotten Names', tribute: 'written names', domain: 'memory' },
          { id: 'oove', name: 'Oove, The Wandering Flame', tribute: 'kindling', domain: 'warmth' },
          { id: 'qui-gar', name: "Qui'Gar, The Deeproot", tribute: 'buried offerings', domain: 'secrets' },
          { id: 'rokhin', name: 'Rokhin, The Storm Singer', tribute: 'songs in rain', domain: 'weather' },
        ],
        note: 'There are hundreds, if not thousands more. Work with your GM to discover them.',
      },
    },
  },

  // ---------------------------------------------------------------------------
  // Five Banners Burning Campaign Frame (from SRD)
  // ---------------------------------------------------------------------------
  'five-banners-burning': {
    id: 'five-banners-burning',
    name: 'Five Banners Burning',
    source: 'srd',
    downtimeOverride: null,

    mechanics: {
      /**
       * Faction Relations - 5-nation diplomacy grid
       * Tracks alliance/neutral/hostile states between nations
       */
      'faction-relations': {
        pattern: FRAME_MECHANIC_TYPES.FACTION_RELATIONS,
        triggerLocation: 'gm-dashboard',
        relationsKey: 'factionRelations',
        nations: ['armada', 'hilltop', 'jesthaen', 'polaris', 'voldaen'],
        relationScale: { min: -3, max: 3 },
        showAssets: true,
        showProblems: true,
        showObjectives: true,
      },

      /**
       * Objective Countdowns - war objective progress tracking
       * Each nation has major (10-step) and minor (4-6 step) countdowns
       */
      'objective-countdown': {
        pattern: FRAME_MECHANIC_TYPES.OBJECTIVE_COUNTDOWN,
        triggerLocation: 'gm-dashboard',
        countdownsKey: 'objectiveCountdowns',
        majorSteps: 10,
        minorStepsRange: [4, 6],
        tickFrequency: 'weekly',
        perNation: true,
        maxActivePerNation: { major: 1, minor: 2 },
      },
    },
  },
  // ---------------------------------------------------------------------------
  // Age of Umbra Campaign Frame (from SRD)
  // ---------------------------------------------------------------------------
  'age-of-umbra': {
    id: 'age-of-umbra',
    name: 'The Age of Umbra',
    source: 'srd',
    downtimeOverride: 'keep-watch',

    mechanics: {
      /**
       * Soul Blight - death raises undead, scar corruption tracking
       * GM spends Fear to raise dead NPCs/PCs as zombies
       */
      'soul-blight': {
        pattern: FRAME_MECHANIC_TYPES.TOKEN_POOL,
        tokenKey: 'soulBlightTokens',
        displayName: 'Soul Blight',
        maxTokens: 10,
        triggerEvent: 'pc-death',
        triggerDescription: 'When a humanoid NPC or PC dies, the GM can spend Fear to raise them as a zombie',
        icon: '💀',
        colorClass: 'bg-gray-900/30 border-gray-600',
        // No checkConfig — scars are narrated by GM, not auto-rolled
        sessionEnd: {
          effect: 'clearTokens',
          clearTokens: true,
          description: 'At session end, reset the Soul Blight tracker.',
        },
        onDeath: {
          effect: 'riseAsZombie',
          description: 'If a PC dies, they rise as a zombie bent on destroying everyone they once cared for. Their blighted soul joins the Umbra.',
        },
      },

      /**
       * Lurking Darkness - d12 roll when resting outside Sacred Pyres
       */
      'lurking-darkness': {
        pattern: FRAME_MECHANIC_TYPES.DICE_CHECK,
        displayName: 'Lurking Darkness',
        description: 'Roll when the party rests outside the safety of a Sacred Pyre',
        icon: '🌑',
        dieType: 'd12',
        colorClass: 'bg-gray-900/30 border-gray-600',
        rollTable: [
          { min: 1, max: 2, result: 'Monstrous Encounter', description: 'Something monstrous found them in the dark. An adversary of the GM\'s choice initiates conflict.' },
          { min: 3, max: 5, result: 'Stalking Presence', description: 'Something terrifying stalks nearby. Gain 2 Fear.' },
          { min: 6, max: 9, result: 'Intensifying Darkness', description: 'The imposing darkness intensifies. Gain 1 Fear.' },
          { min: 10, max: 11, result: 'Undisturbed', description: 'The characters rest undisturbed. No effect.' },
          { min: 12, max: 12, result: 'Hopeful Omen', description: 'The characters stumble upon a hopeful omen. Each PC gains 1 Hope.' },
        ],
      },

      /**
       * Keep Watch - downtime move to replace Lurking Darkness roll
       */
      'keep-watch': {
        pattern: FRAME_MECHANIC_TYPES.DOWNTIME_MOVE,
        displayName: 'Keep Watch',
        description: 'Stay vigilant against what lurks beyond camp. Roll your Hope Die and choose whether to replace the GM\'s Lurking Darkness roll.',
        icon: '👁️',
        colorClass: 'bg-indigo-900/20 border-indigo-600',
        rollType: 'hope-die',
        replacesRoll: 'lurking-darkness',
        replacesRollName: 'Lurking Darkness roll',
        multiplePlayersRule: 'take-highest',
      },

      /**
       * Sacred Pyres - safe zones that repel Umbra creatures
       */
      'sacred-pyres': {
        pattern: FRAME_MECHANIC_TYPES.PASSIVE_INFO,
        displayName: 'Sacred Pyres',
        description: 'Every surviving settlement huddles around a Sacred Pyre sparked by a Blessed Branch. The flame repels all but the most powerful monsters. Resting near a Sacred Pyre skips the Lurking Darkness roll.',
        note: 'Blessed Branch: An ashen relic from the Old World. When ignited, all Enduring present gain 3 Hope and the flame repels monsters as long as it burns.',
      },

      /**
       * Umbra-Touched - adversary modifier for GM reference
       */
      'umbra-touched': {
        pattern: FRAME_MECHANIC_TYPES.PASSIVE_INFO,
        displayName: 'Umbra-Touched Adversaries',
        description: 'Adversaries and environments can gain the Umbra-Touched type. All Umbra-Touched adversaries critically succeed on attacks against a PC on a die roll of 19-20.',
        note: 'Describe how the Umbra has warped their essence and form when tagging an adversary as Umbra-Touched.',
      },

      /**
       * Scar Corruption - passive damage bonus + final scar consequence
       */
      'scar-corruption': {
        pattern: FRAME_MECHANIC_TYPES.PASSIVE_INFO,
        displayName: 'Scar Corruption',
        description: 'As PCs gain scars, they become slowly corrupted by the Umbra. When a PC deals damage, they deal extra damage equal to their number of scars.',
        note: 'When a PC marks their last Hope slot with a scar, they don\'t retire — they succumb to the Umbra\'s influence and charge forever into the dark.',
      },
    },
  },
  // ---------------------------------------------------------------------------
  // Motherboard Campaign Frame (from SRD)
  // ---------------------------------------------------------------------------
  'motherboard': {
    id: 'motherboard',
    name: 'Motherboard',
    source: 'srd',
    downtimeOverride: 'network-required',

    mechanics: {
      /**
       * Tech-as-Magic - rename table for spells/magic/tech
       */
      'tech-as-magic': {
        pattern: FRAME_MECHANIC_TYPES.PASSIVE_INFO,
        displayName: 'Tech-as-Magic',
        description: 'In a Motherboard campaign, all magic is technology. Spells become programs, magic damage becomes tech damage, spellcasters become technomancers.',
        note: 'Characters with a Spellcasting trait should describe their abilities as technological effects rather than magical ones.',
      },

      /**
       * Virus Corruption - Beastbound ranger remnant companion corruption
       */
      'virus-corruption': {
        pattern: FRAME_MECHANIC_TYPES.TOKEN_POOL,
        tokenKey: 'virusCorruption',
        displayName: 'Virus Corruption',
        maxTokens: 4,
        triggerEvent: 'downtime-start',
        triggerDescription: 'At downtime start, roll Fear Die. On a 1, mark a Corruption. At 4 marks, remnant companion becomes violent.',
        icon: '🦠',
        colorClass: 'bg-red-900/20 border-red-600',
        checkConfig: {
          checkDie: 'fear',
          threshold: 'lessThanOrEqual',
          onFail: {
            effect: 'gainScar',
            clearTokens: false,
            description: 'Your remnant companion glitches violently. Mark a Corruption.',
          },
        },
        onDeath: {
          effect: 'companionLost',
          description: 'If all 4 Corruptions are marked, the remnant becomes violent and can no longer be commanded or repaired. Take a new companion with two new Experiences.',
        },
      },

      /**
       * Scrap Gathering - collect scrap from defeated remnants
       */
      'scrap-gathering': {
        pattern: FRAME_MECHANIC_TYPES.RESOURCE_HARVEST,
        triggerLocation: 'post-combat',
        resourceKey: 'scrap',
        yieldSource: null,
        flavorConfig: null,
        ingredientTablesSource: null,
      },

      /**
       * Ikonis Weapons - personalized weapon with augment slots
       */
      'ikonis-weapons': {
        pattern: FRAME_MECHANIC_TYPES.EQUIPMENT_OVERRIDE,
        displayName: 'Ikonis',
        icon: '⚔️',
        colorClass: 'bg-purple-900/20 border-purple-600',
        equipmentKey: 'ikonis',
        baseFeatureSource: 'customMechanics.ikonis.baseFeature',
        augmentSlotsSource: 'customMechanics.ikonis.augmentSlots',
        augmentsSource: 'customMechanics.augments',
      },

      /**
       * Quantum Economy - currency system replacing gold
       */
      'quantum-economy': {
        pattern: FRAME_MECHANIC_TYPES.CURRENCY_OVERRIDE,
        displayName: 'Quantum',
        icon: '💠',
        colorClass: 'bg-cyan-900/20 border-cyan-600',
        currencyKey: 'quantum',
        startingAmount: 5,
        conversionSource: 'customMechanics.quantumEconomy.conversion',
        scrapCategories: 'customMechanics.scrap.categories',
        shopStockConfig: 'customMechanics.quantumEconomy.shopStock',
      },
    },
  },

  // ---------------------------------------------------------------------------
  // Colossus of the Drylands Campaign Frame (from SRD)
  // ---------------------------------------------------------------------------
  'colossus-drylands': {
    id: 'colossus-drylands',
    name: 'Colossus of the Drylands',
    source: 'srd',
    downtimeOverride: null,

    mechanics: {
      /**
       * Mount Selection - flavor-only mount choice at character creation
       */
      'mount-selection': {
        pattern: FRAME_MECHANIC_TYPES.PASSIVE_INFO,
        displayName: 'Mount Selection',
        description: 'All PCs must select a mount at character creation. Unless a character has a feature providing an animal companion, mounts do not change mechanics or provide combat advantages. They provide travel speed and ease of movement.',
        note: 'Select from: Horse, Buffalo, Deer, Elk, Bear, Mountain Lion, Dire Wolf, Rhinoceros, Giant Goat, Giant Jackalope, Giant Rattlesnake, Giant Lizard, Giant Turtle, Giant Toad, Giant Scorpion, Giant Centipede, Shadow Beast, Robotic Mount',
      },

      /**
       * Colossal Adversary - multi-segment boss fight tracker
       */
      'colossal-adversary': {
        pattern: FRAME_MECHANIC_TYPES.COLOSSAL_ADVERSARY,
        displayName: 'Colossal Adversary',
        icon: '🏔️',
        colorClass: 'bg-red-900/20 border-red-600',
        segmentTypes: ['head', 'torso', 'arm', 'leg', 'wing', 'tail', 'body', 'claw', 'shell', 'neck', 'talon'],
        spotlightCost: 1,
      },

      /**
       * Soul Shard Tracker - campaign-level objective countdown
       */
      'soul-shard-tracker': {
        pattern: FRAME_MECHANIC_TYPES.OBJECTIVE_COUNTDOWN,
        triggerLocation: 'gm-campaign-dashboard',
        countdownsKey: 'soulShardCountdowns',
        majorSteps: 9,
        minorStepsRange: [1, 1],
        tickFrequency: 'manual',
        perNation: false,
        maxActivePerNation: { major: 1, minor: 0 },
      },

      /**
       * Essentia Mining - resource gathering for magic crystals
       */
      'essentia-resources': {
        pattern: FRAME_MECHANIC_TYPES.RESOURCE_HARVEST,
        triggerLocation: 'exploration',
        resourceKey: 'essentia',
        yieldSource: null,
        flavorConfig: null,
        ingredientTablesSource: null,
      },

      /**
       * Children of Godfell - reference list of example colossi
       */
      'children-of-godfell': {
        pattern: FRAME_MECHANIC_TYPES.PASSIVE_INFO,
        displayName: 'Children of Godfell',
        description: 'Each of Kudamat\'s nine soul shards is protected by a colossus known as a Child of Godfell. These beings are physical manifestations of the god\'s will, formed by elements of the soul shard\'s surroundings.',
        note: 'See the Reference tab in the Colossal Adversary tracker for the full list of example colossi and their forms.',
      },
    },
  },
};

// ============================================================================
// LOOKUP FUNCTIONS
// ============================================================================

/**
 * Get the full config for a specific frame
 * @param {string} frameId - The frame identifier (e.g., 'beast-feast', 'witherwild')
 * @returns {object|null} - Frame config or null if not found
 */
export const getFrameConfig = (frameId) => {
  return FRAME_MECHANIC_CONFIG[frameId] || null;
};

/**
 * Get all mechanics for a frame
 * @param {string} frameId - The frame identifier
 * @returns {object} - Object of mechanics keyed by mechanic ID, or empty object
 */
export const getFrameMechanics = (frameId) => {
  const frame = FRAME_MECHANIC_CONFIG[frameId];
  if (!frame || !frame.mechanics) return {};
  return frame.mechanics;
};

/**
 * Get a specific mechanic config
 * @param {string} frameId - The frame identifier
 * @param {string} mechanicId - The mechanic identifier (e.g., 'cooking', 'wither-corruption')
 * @returns {object|null} - Mechanic config or null if not found
 */
export const getFrameMechanicConfig = (frameId, mechanicId) => {
  const frame = FRAME_MECHANIC_CONFIG[frameId];
  if (!frame || !frame.mechanics) return null;
  return frame.mechanics[mechanicId] || null;
};

/**
 * Check if a frame has a specific mechanic
 * @param {string} frameId - The frame identifier
 * @param {string} mechanicId - The mechanic identifier
 * @returns {boolean}
 */
export const hasFrameMechanic = (frameId, mechanicId) => {
  return getFrameMechanicConfig(frameId, mechanicId) !== null;
};

/**
 * Get the matching algorithm function by ID
 * @param {string} algorithmId - The algorithm identifier
 * @returns {function|null} - The selectWorstDie function or null
 */
export const getMatchingAlgorithm = (algorithmId) => {
  const algorithm = MATCHING_ALGORITHMS[algorithmId];
  return algorithm?.selectWorstDie || null;
};

/**
 * Check if a frame has a downtime override
 * @param {string} frameId - The frame identifier
 * @returns {string|null} - The downtime override ID or null
 */
export const getDowntimeOverride = (frameId) => {
  const frame = FRAME_MECHANIC_CONFIG[frameId];
  return frame?.downtimeOverride || null;
};

/**
 * Get all frame IDs that are configured
 * @returns {string[]} - Array of frame IDs
 */
export const getConfiguredFrameIds = () => {
  return Object.keys(FRAME_MECHANIC_CONFIG);
};

/**
 * Get content overrides for a frame (homebrew content injection point).
 * Returns an empty object if no overrides are defined — never null.
 * @param {string} frameId - The frame identifier
 * @returns {Object} - Content overrides (equipment, ancestries, classes, etc.)
 */
export const getFrameContentOverrides = (frameId) => {
  const frame = FRAME_MECHANIC_CONFIG[frameId];
  return frame?.contentOverrides || {};
};

/**
 * Register a homebrew frame config at runtime.
 * Merges into FRAME_MECHANIC_CONFIG so it's available to all lookup functions.
 * @param {string} frameId - Unique frame identifier
 * @param {Object} frameConfig - Frame config following the schema vocabulary
 */
export const registerFrameConfig = (frameId, frameConfig) => {
  FRAME_MECHANIC_CONFIG[frameId] = frameConfig;
};
